import { ConfirmDialog } from '@/components/confirm-modal/confirm-dialog'
import { Button } from '@/components/ui/button'
import { useDownloadStore } from '@/stores/downloadStore'
import { ProgressCircle } from '@/utils/streamingDownloader'
import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Trash2, X, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'

export function DownloadManager() {
	const { downloads, isMinimized, setMinimized, removeDownload, clearCompleted, cancelDownload } = useDownloadStore()
	const [cancelTargetId, setCancelTargetId] = useState<string | null>(null)

	const activeDownloads = downloads.filter(d => d.status === 'preparing' || d.status === 'downloading')
	const hasActive = activeDownloads.length > 0

	// Automatically close/clear download manager after 3 seconds if no active downloads are remaining
	useEffect(() => {
		if (downloads.length > 0 && !hasActive) {
			const timer = setTimeout(() => {
				clearCompleted()
			}, 3000)
			return () => clearTimeout(timer)
		}
	}, [downloads.length, hasActive, clearCompleted])

	// Don't render anything if there are no downloads
	if (downloads.length === 0) {
		return null
	}

	const handleCancelConfirm = () => {
		if (cancelTargetId) {
			cancelDownload(cancelTargetId)
			setCancelTargetId(null)
		}
	}

	const cancelTarget = downloads.find(d => d.id === cancelTargetId)

	// Format friendly time format: e.g., "2 min 10 sec"
	const formatETAFriendly = (seconds: number): string => {
		if (seconds === Number.POSITIVE_INFINITY || Number.isNaN(seconds)) {
			return 'Calculating...'
		}
		if (seconds < 60) {
			return `${Math.round(seconds)} sec`
		}
		const minutes = Math.floor(seconds / 60)
		const remainingSeconds = Math.round(seconds % 60)
		if (remainingSeconds === 0) {
			return `${minutes} min`
		}
		return `${minutes} min ${remainingSeconds} sec`
	}

	// Format size: e.g., "50.4 MB"
	const formatSize = (bytes: number): string => {
		if (bytes === 0) {
			return '0 B'
		}
		const k = 1024
		const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
		const i = Math.floor(Math.log(bytes) / Math.log(k))
		return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`
	}

	// Format speed: e.g., "5.4 MB/s"
	const formatSpeed = (bytesPerSecond: number): string => {
		if (bytesPerSecond === 0) {
			return '0 B/s'
		}
		const k = 1024
		const sizes = ['B/s', 'KB/s', 'MB/s', 'GB/s']
		const i = Math.floor(Math.log(bytesPerSecond) / Math.log(k))
		return `${(bytesPerSecond / k ** i).toFixed(1)} ${sizes[i]}`
	}

	return (
		<>
			<div
				className={`fixed bottom-4 right-4 z-[99] w-80 bg-popover shadow-levitating border border-border rounded-[8px] overflow-hidden flex flex-col transition-all duration-300 ${
					isMinimized ? 'h-10' : 'max-h-96 h-auto'
				}`}>
				{/* Header */}
				<div className='h-10 px-3 bg-muted/20 border-b border-border flex items-center justify-between shrink-0 select-none'>
					<span className='text-foreground text-xs font-semibold'>
						{hasActive ? `Downloading (${activeDownloads.length} active)` : 'Downloads complete'}
					</span>
					<div className='flex items-center gap-1'>
						<Button
							variant='ghost'
							size='icon'
							className='h-6 w-6 text-muted-foreground hover:text-foreground'
							onClick={() => setMinimized(!isMinimized)}>
							{isMinimized ? (
								<ChevronUp className='size-(--icon-standalone)' />
							) : (
								<ChevronDown className='size-(--icon-standalone)' />
							)}
						</Button>
						{!hasActive && (
							<Button
								variant='ghost'
								size='icon'
								className='h-6 w-6 text-muted-foreground hover:text-foreground'
								onClick={() => clearCompleted()}>
								<X className='size-(--icon-standalone)' />
							</Button>
						)}
					</div>
				</div>

				{/* Body */}
				{!isMinimized && (
					<div className='flex-1 overflow-y-auto p-2 space-y-2'>
						{downloads.map(item => {
							const downloadedStr = formatSize(item.bytesReceived)
							const totalStr = formatSize(item.totalBytes)
							const sizeStr = `${downloadedStr} / ${totalStr}`

							const metaText =
								item.status === 'preparing'
									? 'Preparing...'
									: item.status === 'downloading'
										? item.showProgress === false
											? item.totalBytes > 0
												? `${sizeStr} • ${formatSpeed(item.speed)}`
												: `${downloadedStr} • ${formatSpeed(item.speed)}`
											: item.totalBytes > 0
												? `${sizeStr} • ${formatSpeed(item.speed)} • ${formatETAFriendly(item.eta)}`
												: `${downloadedStr} • ${formatSpeed(item.speed)}`
										: item.status === 'completed'
											? 'Complete'
											: item.status === 'cancelled'
												? 'Cancelled'
												: item.error || 'Failed'

							return (
								<div key={item.id} className='p-2 border border-border bg-card rounded-[6px] flex items-center gap-2'>
									{/* Left Status Icon */}
									<div className='shrink-0'>
										{item.status === 'completed' ? (
											<CheckCircle2 className='size-5 text-success' />
										) : item.status === 'failed' ? (
											<AlertCircle className='size-5 text-destructive' />
										) : item.status === 'cancelled' ? (
											<XCircle className='size-5 text-muted-foreground' />
										) : (
											<ProgressCircle percent={item.percent} />
										)}
									</div>

									{/* File details */}
									<div className='flex-1 min-w-0'>
										<p className='text-foreground text-xs font-semibold truncate select-none text-left'>
											{item.filename}
											{item.status === 'downloading' && item.showProgress !== false && ` (${item.percent}%)`}
										</p>
										<p className='text-muted-foreground text-[11px] font-normal truncate select-none text-left'>
											{metaText}
										</p>
									</div>

									{/* Action button */}
									<div className='shrink-0'>
										{item.status === 'preparing' || item.status === 'downloading' ? (
											<Button
												variant='ghost'
												size='icon'
												className='h-6 w-6 text-muted-foreground hover:text-destructive'
												onClick={() => setCancelTargetId(item.id)}>
												<X className='size-(--icon-standalone)' />
											</Button>
										) : (
											<Button
												variant='ghost'
												size='icon'
												className='h-6 w-6 text-muted-foreground hover:text-foreground'
												onClick={() => removeDownload(item.id)}>
												<Trash2 className='size-(--icon-standalone)' />
											</Button>
										)}
									</div>
								</div>
							)
						})}
					</div>
				)}
			</div>

			{/* Cancel Confirmation Dialog */}
			<ConfirmDialog
				open={cancelTargetId !== null}
				onOpenChange={open => !open && setCancelTargetId(null)}
				title='Cancel Download?'
				description={`Are you sure you want to cancel the download of '${cancelTarget?.filename}'?`}
				confirmLabel='Cancel Download'
				cancelLabel='Keep Downloading'
				confirmVariant='destructive'
				onConfirm={handleCancelConfirm}
			/>
		</>
	)
}
